import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Switch,
  FormControlLabel,
  Paper,
} from "@mui/material";
import {
  DragIndicator as DragIcon,
  Visibility as VisibleIcon,
  VisibilityOff as HiddenIcon,
  RestartAlt as ResetIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { apiRequest } from "../config/api";
import { AdminNavigationPreferences } from "../types";
import Layout from "../components/Layout";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  visible: boolean;
}

// Sortable item component for drag and drop
interface SortableItemProps {
  item: MenuItem;
  index: number;
  onToggleVisibility: (index: number) => void;
}

const SortableItem: React.FC<SortableItemProps> = ({
  item,
  index,
  onToggleVisibility,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.text });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 1,
        bgcolor: isDragging ? "action.selected" : "background.paper",
        border: isDragging ? "2px solid #1976d2" : "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        boxShadow: isDragging ? 4 : 1,
      }}
      secondaryAction={
        <Box display="flex" alignItems="center" gap={1}>
          <FormControlLabel
            control={
              <Switch
                checked={item.visible}
                onChange={() => onToggleVisibility(index)}
                color="primary"
              />
            }
            label={item.visible ? "Visible" : "Hidden"}
            labelPlacement="start"
            sx={{ mr: 1 }}
          />
          <IconButton
            size="small"
            {...listeners}
            {...attributes}
            sx={{ cursor: "grab" }}
          >
            <DragIcon />
          </IconButton>
        </Box>
      }
    >
      <ListItemIcon>
        {item.visible ? (
          <VisibleIcon color="primary" />
        ) : (
          <HiddenIcon color="disabled" />
        )}
      </ListItemIcon>
      <ListItemText
        primary={item.text}
        primaryTypographyProps={{
          fontWeight: item.visible ? "bold" : "normal",
          color: item.visible ? "text.primary" : "text.disabled",
        }}
      />
    </ListItem>
  );
};

const NavigationSettings: React.FC = () => {
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [originalOrder, setOriginalOrder] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const response = await apiRequest("/api/admin-preferences/navigation");

      if (!response.ok) {
        throw new Error("Failed to fetch preferences");
      }

      const prefs: AdminNavigationPreferences = await response.json();

      // Get default menu items from Layout (we'll hardcode them here for now)
      const defaultMenuItems = [
        "Home",
        "Dashboard",
        "Players",
        "League Days",
        "Tournaments",
        "Rankings",
        "Finances",
        "Penalties & Bonuses",
        "Nations League",
        "Announcements",
        "Scorekeeper Requests",
        "Manage Scorekeepers",
        "Player Accounts",
        "Deletion Requests",
        "Tournament Registrations",
        "Admin Users",
        "Seasons",
        "Court Management",
        "Email Settings",
        "Season Registrations",
        "Transfers",
        "Navigation Settings",
        "Dangerous",
      ];

      // Apply custom order or use default
      let orderedItems = defaultMenuItems;
      if (prefs.menuOrder && prefs.menuOrder.length > 0) {
        // Sort by custom order, then add any new items at the end
        orderedItems = [
          ...prefs.menuOrder.filter((item) => defaultMenuItems.includes(item)),
          ...defaultMenuItems.filter((item) => !prefs.menuOrder.includes(item)),
        ];
      }

      const items: MenuItem[] = orderedItems.map((text) => ({
        text,
        icon: null, // Icons don't matter for settings view
        path: "",
        visible: !prefs.hiddenItems.includes(text),
      }));

      setMenuItems(items);
      setOriginalOrder(orderedItems);
      setLoading(false);
    } catch (err: any) {
      console.error("Error fetching preferences:", err);
      setError(err.message || "Failed to load preferences");
      setLoading(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setMenuItems((items) => {
      const oldIndex = items.findIndex((item) => item.text === active.id);
      const newIndex = items.findIndex((item) => item.text === over.id);

      return arrayMove(items, oldIndex, newIndex);
    });

    setHasChanges(true);
  };

  const handleToggleVisibility = (index: number) => {
    const items = [...menuItems];
    items[index].visible = !items[index].visible;
    setMenuItems(items);
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const preferences: AdminNavigationPreferences = {
        menuOrder: menuItems.map((item) => item.text),
        hiddenItems: menuItems
          .filter((item) => !item.visible)
          .map((item) => item.text),
      };

      const response = await apiRequest("/api/admin-preferences/navigation", {
        method: "PUT",
        body: JSON.stringify(preferences),
      });

      if (!response.ok) {
        throw new Error("Failed to save preferences");
      }

      setSuccess("Navigation preferences saved successfully!");
      setHasChanges(false);
      setTimeout(() => setSuccess(null), 3000);

      // Reload the page to apply changes
      setTimeout(() => window.location.reload(), 1000);
    } catch (err: any) {
      console.error("Error saving preferences:", err);
      setError(err.message || "Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (
      !window.confirm(
        "Are you sure you want to reset navigation to default? This will clear all your customizations.",
      )
    ) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const response = await apiRequest("/api/admin-preferences/navigation", {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Failed to reset preferences");
      }

      setSuccess("Navigation reset to default successfully!");
      setTimeout(() => window.location.reload(), 1000);
    } catch (err: any) {
      console.error("Error resetting preferences:", err);
      setError(err.message || "Failed to reset preferences");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
          </Box>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box>
        <Box mb={3}>
          <Typography variant="h4" gutterBottom>
            ⚙️ Navigation Settings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Customize your admin navigation menu by reordering items and hiding
            those you don't use frequently.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 3 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        <Card>
          <CardContent>
            <Box
              mb={2}
              sx={{
                display: "flex",
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: "space-between",
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 2
              }}
            >
              <Typography variant="h6">Menu Items</Typography>
              <Box sx={{ display: "flex", gap: 1, width: { xs: '100%', sm: 'auto' }, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<ResetIcon />}
                  onClick={handleReset}
                  disabled={saving}
                  size="small"
                  sx={{ flex: { xs: 1, sm: 'initial' } }}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  startIcon={
                    saving ? <CircularProgress size={20} /> : <SaveIcon />
                  }
                  onClick={handleSave}
                  disabled={!hasChanges || saving}
                  size="small"
                  sx={{ flex: { xs: 1, sm: 'initial' } }}
                >
                  {saving ? "Saving..." : "Save"}
                </Button>
              </Box>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                <strong>How to use:</strong>
              </Typography>
              <ul style={{ margin: "8px 0", paddingLeft: "20px", fontSize: window.innerWidth < 600 ? '0.75rem' : '0.875rem' }}>
                <li>Drag items using the handle icon to reorder</li>
                <li>Toggle visibility to show/hide menu items</li>
                <li>Hidden items won't appear in your navigation menu</li>
                <li>Changes apply only to your account</li>
              </ul>
            </Alert>

            <Divider sx={{ mb: 2 }} />

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={menuItems.map((item) => item.text)}
                strategy={verticalListSortingStrategy}
              >
                <List>
                  {menuItems.map((item, index) => (
                    <SortableItem
                      key={item.text}
                      item={item}
                      index={index}
                      onToggleVisibility={handleToggleVisibility}
                    />
                  ))}
                </List>
              </SortableContext>
            </DndContext>
          </CardContent>
        </Card>
      </Box>
    </Layout>
  );
};

export default NavigationSettings;
